// Anke, 2026-07-16 — "Auf 100 % normalisieren": Die aktuelle Ansicht behalten,
// aber die Prozentstufe auf 100 % bringen. Noetig, wenn man versehentlich bei
// einer sehr grossen oder kleinen Stufe angefangen hat und die Weltmasse wieder
// geradeziehen will.
//
// DIE RECHNUNG dahinter, weil sie ueberraschend einfach ist und man sie sonst
// nicht wiederfindet:
//
//   Bildschirm = Welt × Stufe + Verschiebung
//
// Multipliziert man JEDE Weltkoordinate mit der Stufe s und setzt die Stufe
// danach auf 1, bleibt die Verschiebung unberuehrt und es gilt
//
//   (Welt × s) × 1 + Verschiebung  =  Welt × s + Verschiebung
//
// also exakt dasselbe Bild. Der Bezugspunkt ist dabei der WELT-URSPRUNG, nicht
// die Bildmitte — genau deshalb darf die Verschiebung stehen bleiben.
//
// WAS MITSKALIERT WERDEN MUSS: alles, was beim Zoomen mitwaechst — also jeder
// Wert in Welteinheiten. Konva legt die Stufe als Transformation ueber die
// ganze Buehne; Strichstaerken und Schriftgroessen wachsen deshalb mit und
// muessen hier mitgezogen werden, sonst sieht das Bild hinterher NICHT gleich
// aus.
//
// WAS NICHT: Verhaeltnisse (line.midRatio), Stufen-Angaben (person.
// circleStrokeWidth ist eine Rahmenstaerke-STUFE 0/1/2/4/8, keine Breite) und
// die Drehung.
//
// BEKANNTE ABWEICHUNGEN (mit Anke am 2026-07-16 abgestimmt): Schatten und
// Haftnotiz-Schrift stehen NICHT am Objekt, sondern als feste Werte im
// Zeichencode. Sie lassen sich hier nicht mitziehen und fallen aufs
// Normalmass zurueck — beim Normalisieren erwuenscht.
//
// Textbox-Innenabstand (`paddingV`/`paddingH`) ist SEIT 2026-08-16 eine
// Ausnahme von der obigen Abweichung: die Felder stehen jetzt am Objekt
// (bei Erstellung eingefroren, siehe `textbox.interface.ts`) und werden
// deshalb wie jeder andere Welteinheiten-Wert mitskaliert (review 05,
// Anke, 2026-08-17: "padding beim normalisieren mitskalieren") — sonst
// verschiebt sich nach dem Normalisieren sichtbar das Verhaeltnis von
// Innenabstand zu Boxgroesse.

import type { BoardObject } from '../interfaces/board-object.interface.js';
import type { TextDocument, TextDocumentNode, TextDocumentMark } from '../../features/textbox/text-document.type.js';

/** Nur absolute px-Werte; alles andere bleibt unberuehrt (siehe scaleMark). */
const PX_VALUE = /^([\d.]+)px$/;

/**
 * Skaliert eine einzelne `textStyle.fontSize`-Markierung.
 *
 * Nur `px` wird angefasst. Relative Einheiten (`em`) muessen stehen bleiben:
 * Die Hoch-/Tiefstellung nutzt `0.75em`, und ein multipliziertes `em` wuerde
 * ihre Groesse zerlegen. Dieselbe Regel wie im Raster-Code
 * (`textbox-svg-renderer.ts`, scaleRasterFontSizeMarks) — dort steht sie mit
 * derselben Begruendung.
 */
function scaleMark(mark: TextDocumentMark, k: number): TextDocumentMark {
  if (mark.type !== 'textStyle') return mark;
  const raw = mark.attrs?.['fontSize'];
  if (typeof raw !== 'string') return mark;
  const match = PX_VALUE.exec(raw.trim());
  if (!match) return mark;
  return {
    ...mark,
    attrs: { ...mark.attrs, fontSize: `${parseFloat(match[1]!) * k}px` },
  };
}

function scaleNode(node: TextDocumentNode, k: number): TextDocumentNode {
  const marks = node.marks?.map((m) => scaleMark(m, k));
  const content = node.content?.map((c) => scaleNode(c, k));
  if (!marks && !content) return node;
  return {
    ...node,
    ...(marks ? { marks } : {}),
    ...(content ? { content } : {}),
  };
}

/**
 * Skaliert alle Schriftgroessen-Markierungen im Text-Dokument einer Textbox.
 *
 * Noetig ZUSAETZLICH zu `obj.fontSize`: Das Raster multipliziert die
 * gespeicherten Markierungen mit der aktuellen Zoomstufe — die wir gerade auf 1
 * setzen. Ohne diesen Schritt wuerde jeder Textabschnitt mit eigener
 * Schriftgroesse beim Normalisieren zusammenschrumpfen.
 */
export function scaleTextDocument(doc: TextDocument, k: number): TextDocument {
  const content = doc.content?.map((n) => scaleNode(n, k));
  return content ? { ...doc, content } : doc;
}

/**
 * Liefert das Objekt um `k` skaliert — Bezugspunkt ist der Welt-Ursprung.
 *
 * Rein: `obj` wird nicht angefasst, es kommt ein neues Objekt zurueck.
 */
export function scaleObjectBy(obj: Readonly<BoardObject>, k: number): BoardObject {
  const base = {
    ...obj,
    x: obj.x * k,
    y: obj.y * k,
    width: obj.width * k,
    height: obj.height * k,
    // rotation bleibt: Eine gleichmaessige Skalierung um den Ursprung dreht
    // nichts.
  } as BoardObject;

  switch (base.type) {
    case 'line':
      return {
        ...base,
        x1: base.x1 * k,
        y1: base.y1 * k,
        x2: base.x2 * k,
        y2: base.y2 * k,
        strokeWidth: base.strokeWidth * k,
        ...(base.curveControls
          ? { curveControls: base.curveControls.map((p) => ({ x: p.x * k, y: p.y * k })) }
          : {}),
        // midRatio ist ein Verhaeltnis (0..1) — bleibt.
      };

    case 'text':
      return {
        ...base,
        fontSize: base.fontSize * k,
        borderWidth: base.borderWidth * k,
        ...(base.cornerRadius !== undefined ? { cornerRadius: base.cornerRadius * k } : {}),
        ...(base.paddingV !== undefined ? { paddingV: base.paddingV * k } : {}),
        ...(base.paddingH !== undefined ? { paddingH: base.paddingH * k } : {}),
        ...(base.document ? { document: scaleTextDocument(base.document, k) } : {}),
      };

    case 'rectangle':
      return {
        ...base,
        strokeWidth: base.strokeWidth * k,
        cornerRadius: base.cornerRadius * k,
      };

    case 'ellipse':
      return { ...base, strokeWidth: base.strokeWidth * k };

    case 'person':
      // NUR fontSize. `circleStrokeWidth` ist eine STUFE (0/1/2/4/8), keine
      // Breite — multiplizieren wuerde den Rahmen-STIL veraendern, nicht seine
      // Groesse. Ist keine Schriftgroesse gesetzt, greift im Renderer eine
      // Konstante; die kann hier nicht mitgezogen werden (dann bleibt es beim
      // Normalmass, wie bei den Schatten).
      return base.fontSize !== undefined ? { ...base, fontSize: base.fontSize * k } : base;

    // sticky   — Schrift wird aus der Zettelgroesse errechnet, nichts zu tun.
    // smiley   — vollstaendig aus width/height abgeleitet.
    // checkbox — vollstaendig aus width abgeleitet.
    // image    — dataUrl + width/height.
    // rect     — Test-Objekt ohne eigene Masse.
    default:
      return base;
  }
}
